// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { useState } from 'react';

interface Props {
  onSubmit: (code: string, isRecovery: boolean) => void;
  error?: string;
  loading?: boolean;
}

export default function TotpLoginChallenge({ onSubmit, error, loading }: Props) {
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    onSubmit(code.trim(), useRecovery);
  };

  return (
    <div className="space-y-4" data-testid="totp-login-prompt">
      <h2 className="text-lg font-semibold text-gray-900">Two-Factor Authentication</h2>
      <p className="text-sm text-gray-600">
        {useRecovery
          ? 'Enter one of your recovery codes to sign in.'
          : 'Enter the 6-digit code from your authenticator app.'}
      </p>
      <form onSubmit={handleSubmit} className="space-y-3">
        {useRecovery ? (
          <input
            type="text"
            data-testid="totp-recovery-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Recovery code"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- the recovery-code field is the only thing on this step; focusing it saves every user, keyboard and screen-reader included, a hunt for it
            autoFocus
          />
        ) : (
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            data-testid="totp-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            className="w-32 rounded-lg border border-gray-300 px-3 py-2 text-center text-lg font-mono tracking-widest focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- the six-digit code field is the only thing on this step
            autoFocus
          />
        )}
        {error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="totp-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading || !code.trim()}
          data-testid="totp-submit"
          className={`w-full rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-brand-200 ${
            loading ? 'bg-slate-400 cursor-not-allowed' : 'bg-brand-600 hover:bg-brand-700'
          }`}
        >
          {loading ? 'Verifying...' : 'Verify'}
        </button>
      </form>
      <button
        type="button"
        onClick={() => {
          setUseRecovery(!useRecovery);
          setCode('');
        }}
        data-testid="totp-toggle-recovery"
        className="text-sm text-brand-600 hover:underline"
      >
        {useRecovery ? 'Use authenticator code' : 'Use recovery code'}
      </button>
    </div>
  );
}
